import type { JSONSchema7 } from 'json-schema';

import type { DeepReadonly } from './types';
// We want these to match the API schema as closely as possible

export const openAiJsonSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    store: {
      type: ['boolean', 'null'],
      description: 'Whether or not to store the output of this chat completion request.',
      default: false,
    },
    metadata: {
      type: ['object', 'null'],
      description: 'Developer-defined tags and values used for filtering completions in the dashboard.',
    },
    frequency_penalty: {
      type: ['number', 'null'],
      description: 'Penalize new tokens based on their existing frequency.',
      default: 0,
      minimum: -2,
      maximum: 2,
    },
    logit_bias: {
      type: ['object', 'null'],
      description: 'Modify the likelihood of specified tokens appearing in the completion.',
      additionalProperties: {
        type: 'number',
        minimum: -100,
        maximum: 100,
      },
    },
    logprobs: {
      type: ['boolean', 'null'],
      description: 'Whether to return log probabilities of output tokens.',
      default: false,
    },
    top_logprobs: {
      type: ['integer', 'null'],
      description: 'Number of most likely tokens to return at each position.',
      minimum: 0,
      maximum: 20,
    },
    max_tokens: {
      type: ['integer', 'null'],
      description: 'Deprecated: Maximum tokens to generate.',
      default: null,
    },
    max_completion_tokens: {
      type: ['integer', 'null'],
      description: 'Upper bound for the number of tokens that can be generated.',
    },
    n: {
      type: ['integer', 'null'],
      description: 'How many chat completion choices to generate.',
      default: 1,
    },
    modalities: {
      type: ['array', 'null'],
      description: 'Output types the model should generate.',
      items: {
        type: 'string',
        enum: ['text', 'audio'],
      },
    },
    prediction: {
      type: ['object', 'null'],
      description: 'Configuration for predicted output.',
      additionalProperties: true,
    },
    audio: {
      type: ['object', 'null'],
      description: 'Parameters for audio output.',
      additionalProperties: true,
    },
    presence_penalty: {
      type: ['number', 'null'],
      description: 'Penalize new tokens based on their presence in the text.',
      default: 0,
      minimum: -2,
      maximum: 2,
    },
    response_format: {
      type: 'object',
      description: 'Format the model must output.',
      properties: {
        type: {
          type: 'string',
          enum: ['json_schema', 'json_object'],
          description: 'Format type.',
        },
        json_schema: {
          type: ['object', 'null'],
          description: 'JSON schema for structured outputs.',
          additionalProperties: true,
        },
      },
    },
    seed: {
      type: ['integer', 'null'],
      description: 'Seed for deterministic sampling.',
    },
    service_tier: {
      type: ['string', 'null'],
      description: 'Specifies latency tier for request processing.',
      default: 'auto',
    },
    stop: {
      type: ['string', 'array', 'null'],
      description: 'Sequences where the API stops generating further tokens.',
      items: {
        type: 'string',
      },
    },
    stream: {
      type: ['boolean', 'null'],
      description: 'If true, sends partial message deltas.',
      default: false,
    },
    stream_options: {
      type: ['object', 'null'],
      description: 'Options for streaming response.',
      additionalProperties: true,
    },
    temperature: {
      type: ['number', 'null'],
      description: 'Sampling temperature to use.',
      default: 1,
      minimum: 0,
      maximum: 2,
    },
    top_p: {
      type: ['number', 'null'],
      description: 'Nucleus sampling probability.',
      default: 1,
      minimum: 0,
      maximum: 1,
    },
    tools: {
      type: 'array',
      description: 'List of tools the model may call.',
      items: {
        type: 'object',
        additionalProperties: true,
      },
    },
    tool_choice: {
      type: ['string', 'object'],
      description: 'Controls which tool is called by the model.',
      oneOf: [
        { type: 'string', enum: ['none', 'auto', 'required'] },
        {
          type: 'object',
          properties: {
            type: { type: 'string', enum: ['function'] },
            function: {
              type: 'object',
              properties: { name: { type: 'string' } },
              required: ['name'],
            },
          },
        },
      ],
    },
    parallel_tool_calls: {
      type: 'boolean',
      description: 'Enable parallel function calling.',
      default: true,
    },
    reasoning: {
      type: ['object', 'null'],
      description: 'Configuration for reasoning models (o-series). Controls reasoning effort and summary output.',
      properties: {
        effort: {
          type: ['string', 'null'],
          description: 'Constrains effort on reasoning. Possible values: low, medium, high.',
          enum: ['low', 'medium', 'high'],
          default: 'medium',
        },
        summary: {
          type: ['string', 'null'],
          description: 'Controls the reasoning summary output. Possible values: auto, concise, detailed.',
          enum: ['auto', 'concise', 'detailed'],
        },
      },
      additionalProperties: false,
    },
    user: {
      type: 'string',
      description: 'Unique identifier for the end-user.',
    },
  },
  additionalProperties: false,
} as const;

// this confirms that the type is a valid JSON schema, but we want it as a `const` so we can use it to index the properties
export const openAiSchemaProperties: DeepReadonly<JSONSchema7> = openAiJsonSchema;

export const retrieveOpenAiParameters = (): string[] => Object.keys(openAiJsonSchema.properties);
